import { showDangerDialog, showPortControlRecaptchaErrorDialog } from "../common";
import { PermitType } from "../common/enums";
import { HotWorkOnShipPermitFormState_default, HotWorkOnShipPermitFormState_populateErrorMessages, HotWorkOnShipPermitFormState_toRequest } from "../types/forms/Permits/HotWorkOnShipPermitFormState";
import { AnonymousPermitFormProps } from "../types/forms/Permits/PermitFormProps";
import { HotWorkOnShipPermit_toFormState } from "../types/permits/HotWorkOnShipPermit";
import usePortControlPermitForm from "./usePortControlPermitForm";

function useHotWorkOnShipPermitForm(props: AnonymousPermitFormProps) {
    const baseFormManager = usePortControlPermitForm({
        ...props,
        permitType: PermitType.HotWorkOnShipPermit,
        populateErrorMessages: HotWorkOnShipPermitFormState_populateErrorMessages,
        toFormState: HotWorkOnShipPermit_toFormState,
        toRequest: HotWorkOnShipPermitFormState_toRequest,
        defaultFormState: HotWorkOnShipPermitFormState_default,
        disableDataFetch: props.isAnonymousPermit,
        clearErrorMessages: formState => HotWorkOnShipPermitFormState_populateErrorMessages(formState, {})
    });

    const { isNewPermit, storedPermit, formState } = baseFormManager;
    const { resources } = props;

    async function submitCreateAnonymousPermitHandlerAsync(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        const grecaptcha = (window as any).grecaptcha;
        if (!grecaptcha) {
            console.error("No grecaptcha found.");
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("RefreshPageAndTryAgain"));
            return;
        }

        if (!props.recaptchaPublicSiteKey) {
            console.error("No recaptcha public site key found.");
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("RefreshPageAndTryAgain"));
            return;
        }

        const recaptchaToken = await grecaptcha.enterprise.execute(props.recaptchaPublicSiteKey);
        const request = HotWorkOnShipPermitFormState_toRequest(formState);
        request.recaptchaToken = recaptchaToken;

        await baseFormManager.createHandlerAsync(request, {
            onSuccess: () => {
                window.location.href = "/Permits/AnonymousPermitConfirmation";
            },
            onError: (problemDetails) => {
                if (!problemDetails.errors) {
                    showDangerDialog(
                        props.resources.get("SomethingWentWrong"),
                        props.resources.get("RefreshPageAndTryAgain")
                    );
                    return;
                }

                const isRecaptchaError = Object.keys(problemDetails.errors).includes("RecaptchaToken");
                if (isRecaptchaError) {
                    showPortControlRecaptchaErrorDialog(resources);
                    return;
                }

                baseFormManager.handleValidationErrors(problemDetails);
            }
        });
    }

    const submitHandler =
        props.isAnonymousPermit && isNewPermit
            ? submitCreateAnonymousPermitHandlerAsync
            : baseFormManager.submitHandler;

    const showAnonymousUserEmail =
        (props.isAnonymousPermit && isNewPermit) || (!!storedPermit && storedPermit.isAnonymousPermit);

    const canUpdateWithNotification = !!storedPermit && storedPermit.isAnonymousPermit && storedPermit.canUpdate;

    return {
        ...baseFormManager,
        showAnonymousUserEmail,
        canUpdateWithNotification,
        submitHandler
    };
}

export default useHotWorkOnShipPermitForm;
